import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import SEO from '../components/SEO';
import { getOrganizationSchema, getWebSiteSchema } from '../utils/structuredData';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowRight, BookOpen, Package, Calculator, Bot, Users, Briefcase, GraduationCap, Instagram } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Home = () => {
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [blogPosts, setBlogPosts] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchFeaturedPosts();
  }, [language]);

  const fetchFeaturedPosts = async () => {
    try {
      const response = await axios.get(`${API}/blog?featured=true&language=${language}`);
      setBlogPosts(response.data.slice(0, 3));
    } catch (error) {
      console.error('Failed to fetch posts:', error);
    }
  };

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/newsletter`, {
        email,
        first_name: firstName,
        language
      });
      toast.success(language === 'ro' ? 'Gata, ești în gașcă! 🎉' : 'Successfully subscribed!');
      setEmail('');
      setFirstName('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to subscribe');
    } finally {
      setLoading(false);
    }
  };

  const seoTitle = language === 'ro' 
    ? 'FinanciallyFree.ro - Libertate financiară, simplu ca bună ziua'
    : 'FinanciallyFree.ro - Financial Freedom, Side Hustles & Investing';
    
  const seoDescription = language === 'ro'
    ? 'Fără bullshit, fără scheme dubioase. Doar ghiduri practice despre cum să-ți iei banii în mână, side hustle-uri testate și strategii care chiar funcționează.'
    : 'Build your financial freedom with practical guides on saving, investing, side hustles, and FIRE. Free financial education in Romanian and English.';

  const structuredData = [
    getOrganizationSchema(),
    getWebSiteSchema()
  ];

  return (
    <>
      <SEO 
        title={seoTitle}
        description={seoDescription}
        keywords="libertate financiară, FIRE, side hustle, investiții, economisire, educație financiară România"
        url="/"
        structuredData={structuredData}
      />
      <div className="min-h-screen py-8">
        {/* Hero Section */}
        <section className="relative overflow-hidden bg-gradient-to-br from-blue-50 via-mint-50 to-white pt-24 pb-20 md:pt-32 md:pb-32">
        <div className="container mx-auto px-4">
          <div className="grid gap-12 items-center lg:grid-cols-2">
            {/* Left: Text Content */} 
            <div className="text-center lg:text-left">
              <h1 className="mb-6 text-4xl font-bold leading-tight text-slate-900 sm:text-5xl lg:text-6xl" data-testid="hero-title">
                {t('hero.title')}
              </h1>
              <p className="mb-8 text-lg text-slate-600 sm:text-xl">
                {t('hero.subtitle')}
              </p>
              <div className="flex flex-col justify-center gap-4 sm:flex-row lg:justify-start">
                <Button
                  size="lg"
                  className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 px-8 text-lg shadow-lg hover:shadow-xl transition-all"
                  onClick={() => navigate('products/pachet-de-pornire-spre-libertatea-financiara')}
                  data-testid="primary-cta"
                >
                  {t('hero.primaryCta')}
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  className="px-8 text-lg border-2 hover:bg-blue-50"
                  onClick={() => navigate('/products')}
                  data-testid="secondary-cta"
                >
                  {t('hero.secondaryCta')}
                </Button>
              </div>
            </div>

            {/* Right: Hero Image */}
            <div className="relative">
              <div className="relative rounded-3xl overflow-hidden shadow-2xl animate-float">
                <img
                  src="https://images.unsplash.com/photo-1579621970563-ebec7560ff3e?w=800&q=80"
                  alt="Financial Freedom"
                  className="w-full h-auto"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-blue-900/20 to-transparent"></div>
              </div>
              {/* Decorative Elements */}
              <div className="absolute -top-4 -right-4 w-24 h-24 bg-gradient-to-br from-mint-400 to-mint-500 rounded-full opacity-20 blur-2xl"></div>
              <div className="absolute -bottom-4 -left-4 w-32 h-32 bg-gradient-to-br from-blue-400 to-blue-500 rounded-full opacity-20 blur-2xl"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Who Are We */}
      <section className="py-16 md:py-24">
        <div className="container mx-auto px-4">
          <div className="mx-auto max-w-3xl text-center">
            <h2 className="mb-4 text-3xl font-bold text-slate-900 sm:text-4xl">
              {t('home.whoAreWe')}
            </h2>
            <p className="text-lg text-slate-600">
              {t('home.whoAreWeDesc')}
            </p>
          </div>
        </div>
      </section>

      {/* For Who */}
      <section className="bg-gradient-to-br from-white via-blue-50 to-mint-50 py-16 md:py-24">
        <div className="container mx-auto px-4">
          <h2 className="mb-12 text-center text-3xl font-bold text-slate-900 sm:text-4xl">
            {t('home.forWho')}
          </h2>
          <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-4">
            {[
              {
                icon: <GraduationCap className="h-8 w-8 text-blue-600" />,
                title: language === 'ro' ? 'Tineri la început de drum' : 'Young Professionals',
                desc: language === 'ro' ? 'Vrei să faci primii pași spre libertate financiară? Perfect.' : 'Building a solid financial foundation'
              },
              {
                icon: <Users className="h-8 w-8 text-mint-600" />,
                title: language === 'ro' ? 'Părinți' : 'Parents',
                desc: language === 'ro' ? 'Vrei un viitor mai bun pentru familie? Ai venit unde trebuie.' : 'Planning for the family future'
              },
              {
                icon: <Briefcase className="h-8 w-8 text-blue-600" />,
                title: 'Freelanceri',
                desc: language === 'ro' ? 'Venituri variabile? Te ajutăm să pui banii la treabă.' : 'Building financial stability'
              },
              {
                icon: <Users className="h-8 w-8 text-mint-600" />,
                title: language === 'ro' ? 'Angajați' : 'Employees',
                desc: language === 'ro' ? 'Vrei extra cash pe lângă salariu? Avem idei.' : 'Seeking additional income'
              }
            ].map((item, idx) => (
              <Card key={idx} className="border-0 bg-white shadow-md transition-shadow hover:shadow-lg">
                <CardContent className="p-6 text-center">
                  <div className="mb-4 flex justify-center">{item.icon}</div>
                  <h3 className="mb-2 text-lg font-semibold text-slate-900">{item.title}</h3>
                  <p className="text-slate-600">{item.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* What You'll Find */}
      <section className="py-16 md:py-24">
        <div className="container mx-auto px-4">
          <h2 className="mb-12 text-center text-3xl font-bold text-slate-900 sm:text-4xl">
            {t('home.whatYouFind')}
          </h2>
          <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-4">
            {[
              { icon: <BookOpen />, label: language === 'ro' ? 'Blog & Ghiduri' : 'Blog & Guides', path: '/blog' },
              { icon: <Package />, label: language === 'ro' ? 'Produse Digitale' : 'Digital Products', path: '/products' },
              { icon: <Calculator />, label: language === 'ro' ? 'Calculatoare' : 'Tools & Calculators', path: '/tools' },
              { icon: <Bot />, label: language === 'ro' ? 'AI Money Mentor' : 'AI Money Mentor', path: '/ai-money-mentor' }
            ].map((item, idx) => (
              <Card
                key={idx}
                className="group cursor-pointer border-0 bg-gradient-to-br from-blue-50 to-mint-50 transition-all hover:shadow-xl"
                onClick={() => navigate(item.path)}
              >
                <CardContent className="p-8 text-center">
                  <div className="mb-4 flex justify-center text-blue-600 transition-transform group-hover:scale-110">
                    {React.cloneElement(item.icon, { className: 'h-12 w-12' })}
                  </div>
                  <h3 className="text-lg font-semibold text-slate-900">{item.label}</h3>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Popular Posts */}
      <section className="bg-gradient-to-br from-mint-50 via-white to-blue-50 py-16 md:py-24">
        <div className="container mx-auto px-4">
          <h2 className="mb-12 text-center text-3xl font-bold text-slate-900 sm:text-4xl">
            {t('home.popularPosts')}
          </h2>
          <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
            {blogPosts.map((post) => (
              <Card
                key={post.id}
                className="group cursor-pointer overflow-hidden border-0 bg-white shadow-md transition-all hover:shadow-xl"
                onClick={() => navigate(`/blog/${post.slug}`)}
                data-testid={`blog-post-${post.slug}`}
              >
                {post.image_url && (
                  <div className="h-48 overflow-hidden">
                    <img
                      src={post.image_url}
                      alt={post.title}
                      className="h-full w-full object-cover transition-transform group-hover:scale-105"
                    />
                  </div>
                )}
                <CardContent className="p-6">
                  <div className="mb-2 text-sm font-medium text-blue-600">{post.category}</div>
                  <h3 className="mb-2 text-xl font-bold text-slate-900 group-hover:text-blue-600">
                    {post.title}
                  </h3>
                  <p className="mb-4 text-slate-600">{post.subtitle}</p>
                  <div className="text-sm text-slate-500">{post.author}</div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Newsletter */}
      <section className="py-16 md:py-24">
        <div className="container mx-auto px-4">
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="mb-4 text-3xl font-bold text-slate-900 sm:text-4xl">
              {t('home.newsletter')}
            </h2>
            <p className="mb-8 text-lg text-slate-600">{t('home.newsletterDesc')}</p>
            <form onSubmit={handleNewsletterSubmit} className="flex flex-col gap-4 sm:flex-row">
              <Input
                type="text"
                placeholder={language === 'ro' ? 'Nume' : 'First name'}
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                className="flex-1"
                data-testid="newsletter-firstname"
              />
              <Input
                type="email"
                placeholder="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="flex-1"
                data-testid="newsletter-email"
              />
              <Button
                type="submit"
                disabled={loading}
                className="bg-gradient-to-r from-blue-600 to-mint-600 hover:from-blue-700 hover:to-mint-700 shadow-lg"
                data-testid="newsletter-submit"
              >
                {language === 'ro' ? 'Bagă-mă-n gașcă' : 'Subscribe'}
              </Button>
            </form>
          </div>
        </div>
      </section>

      {/* Instagram Feed */}
      <section className="bg-gradient-to-br from-blue-50 via-white to-mint-50 py-16 md:py-24">
        <div className="container mx-auto px-4">
          <div className="mb-12 text-center">
            <h2 className="mb-4 text-3xl font-bold text-slate-900 sm:text-4xl">
              {language === 'ro' ? 'Hai pe Instagram!' : 'Follow us on Instagram'}
            </h2>
            <p className="text-lg text-slate-600">
              {language === 'ro' 
                ? 'Tips zilnice despre bani și cum să-ți trăiești viața pe bune' 
                : 'Daily tips on financial freedom and inspiration'}
            </p>
            <a
              href="https://www.instagram.com/financiallyfree.official"
              target="_blank"
              rel="noopener noreferrer"
              className="mt-4 inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-pink-500 to-purple-600 px-6 py-3 text-white font-semibold shadow-lg hover:shadow-xl transition-all hover:scale-105"
            >
              <Instagram className="h-5 w-5" />
              @financiallyfree.official
            </a>
          </div>
          
          {/* Instagram Embed */}
          <div className="mx-auto max-w-4xl">
            <Card className="border-0 shadow-2xl overflow-hidden">
              <CardContent className="p-0">
                <div className="bg-gradient-to-br from-pink-50 to-purple-50 p-8 text-center">
                  <div className="mb-6">
                    <div className="mx-auto mb-4 flex h-20 w-20 items-center justify-center rounded-full bg-gradient-to-br from-pink-500 to-purple-600">
                      <Instagram className="h-10 w-10 text-white" />
                    </div>
                    <h3 className="text-2xl font-bold text-slate-900">@financiallyfree.official</h3>
                  </div>
                  
                  {/* Instagram Feed Grid Preview */}
                  <div className="grid grid-cols-3 gap-2 rounded-xl overflow-hidden">
                    <div className="aspect-square bg-gradient-to-br from-blue-400 to-mint-400 rounded-lg flex items-center justify-center">
                      <span className="text-white text-4xl">💰</span>
                    </div>
                    <div className="aspect-square bg-gradient-to-br from-mint-400 to-blue-400 rounded-lg flex items-center justify-center">
                      <span className="text-white text-4xl">📈</span>
                    </div>
                    <div className="aspect-square bg-gradient-to-br from-blue-400 to-purple-400 rounded-lg flex items-center justify-center">
                      <span className="text-white text-4xl">🎯</span>
                    </div>
                    <div className="aspect-square bg-gradient-to-br from-purple-400 to-pink-400 rounded-lg flex items-center justify-center">
                      <span className="text-white text-4xl">💡</span>
                    </div>
                    <div className="aspect-square bg-gradient-to-br from-pink-400 to-blue-400 rounded-lg flex items-center justify-center">
                      <span className="text-white text-4xl">🚀</span>
                    </div>
                    <div className="aspect-square bg-gradient-to-br from-blue-400 to-mint-400 rounded-lg flex items-center justify-center">
                      <span className="text-white text-4xl">✨</span>
                    </div>
                  </div>
                  
                  <p className="mt-6 text-sm text-slate-600">
                    {language === 'ro' 
                      ? 'Click pe butonul de mai sus pentru a vedea toate postările noastre' 
                      : 'Click the button above to see all our posts'}
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </div>
    </>
  );
};

export default Home;